<template>
  <fieldset
    class="product-swatches"
    :class="[$blockClass(blockClass)]"
  >
    <!-- COLOR -->
    <div
      v-if="optionIsColor"
      class="product-swatches__color"
    >
      <div class="product-swatches__container">
        <legend
          v-if="!productCard"
          class="product-swatches__legend caption"
        >
          <div class="product-swatches__legend-content">
            <div class="product-swatches__label">
              <div
                class="product-swatches__name text-heading-l"
                v-text="$string('product.swatch.color_option')"
              />

              <div
                class="product-swatches__value text-heading-l"
                v-text="selectedOption?.value"
              />
            </div>

            <slot name="link" />
          </div>
        </legend>

        <div class="product-swatches__list">
          <component
            :is="element"
            v-for="(value) of option.values"
            :key="value"
            :href="href(value)"
            class="product-swatches__item"
            :class="getSwatchItemClasses(value)"
            :data-value="value.toLowerCase()"
            @click="handleSwatchClick($event, value)"
          >
            <input
              v-if="!productCard"
              :id="`${option.name}-${value}`"
              class="product-swatches__item-input visually-hidden"
              :name="option.name"
              type="radio"
              :value="value"
              :checked="value === selectedOption?.value"
              @change="handleOptionChange(value)"
            >

            <label
              class="product-swatches__item-label"
              :for="`${option.name}-${value}`"
            >
              <div class="product-swatches__item-image-container">
                <span
                  ref="swatchItems"
                  class="product-swatches__item-image"
                  :class="{
                    [whiteSwatchClass]: isWhiteSwatch(value)
                  }"
                  :style="getSwatchItemStyle(value)"
                />
              </div>
            </label>
          </component>
        </div>
      </div>
    </div>

    <!-- MODEL -->
    <div
      v-else-if="optionIsVariant"
      class="product-swatches__model"
    >
      <legend class="product-swatches__legend caption">
        <div class="product-swatches__legend-content">
          <span
            class="product-swatches__label text-heading-l"
            v-text="$string('product.swatch.model_option')"
          />

          <slot name="link" />
        </div>
      </legend>

      <select
        v-model="selected"
        class="product-swatches__select"
      >
        <option
          v-for="(value) of option.values"
          :key="value"
          class="product-swatches__option"
          :value="value"
          v-text="value"
        />
      </select>
    </div>

    <!-- GIFT CARD -->
    <div
      v-else-if="optionIsGiftCard"
      class="product-swatches__model"
    >
      <legend class="product-swatches__legend caption">
        <div class="product-swatches__legend-content">
          <span
            class="product-swatches__label text-heading-l"
            v-text="$string('product.swatch.gift_card_option')"
          />

          <slot name="link" />
        </div>
      </legend>

      <select
        v-model="selected"
        class="product-swatches__select"
      >
        <option
          v-for="(value) of option.values"
          :key="value"
          class="product-swatches__option"
          :value="value"
          v-text="value"
        />
      </select>
    </div>
  </fieldset>
</template>

<script>

/**
 * Vue: Product swatches (product-swatches)
 * -----------------------------------------------------------------------------
 * Product swatches to handle displaying and selecting product options.
 *
 * @param {Object} product - Formatted product object.
 * @param {Object} option - Option being rendered.
 * @param {Array} selectedOptions - Currently selected options.
 * @param {Boolean} showSwatchError - Show swatch error message for this option.
 * @param {Boolean} productCard - Check if it's a product card.
 *
 */

import product from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'

export default {
  name: 'ProductSwatches',

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    images: {
      type: Array,
      default: () => [],
    },
    option: {
      type: [Boolean, Object],
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    product: {
      type: [
        Boolean,
        Object,
      ],
      default: false,
    },
    selectedOptions: {
      type: Array,
      default() {
        return []
      },
    },
    // showSwatchError: {
    //   type: Boolean,
    //   default: false,
    // },
    productCard: {
      type: Boolean,
      default: false,
    },
    swatchBehavior:{
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'option-change': () => true,
  },

  data() {
    return {
      whiteSwatchClass: 'product-swatches__input-label-inner--white',
      selected: this.selectedOptions[this.option.index]?.value,
    }
  },

  computed: {

    /**
     * Compute element.
     * @returns {String}
     */
    element() {
      return this.productCard ? 'a' : 'div'
    },

    /**
     * Compute if the current option is a color.
     * - Used to switch swatches to color swatches.
     * @returns {Boolean}
     */
    optionIsColor() {
      const colorOption = this.$variable('productOptionsName.colorOption').toLowerCase()
      return this.option.name?.toLowerCase() === colorOption
    },

    /**
     * Compute if the current option is a variant.
     * @returns {Boolean}
     */
    optionIsVariant() {
      const modelOption = this.$variable('productOptionsName.modelOption').toLowerCase()
      return this.option.name?.toLowerCase() === modelOption
    },

    /**
     * Compute if the current option is a gift card.
     * @returns {Boolean}
     */
    optionIsGiftCard() {
      const giftCardOption = this.$variable('productOptionsName.giftCardOption').toLowerCase()
      return this.option.name?.toLowerCase() === giftCardOption
    },

    /**
     * Compute text for swatch label
     * @returns {String}
     */
    optionLabelText() {
      const string = this.$string(
        'product.swatch.select_option',
        {
          replace: { option: this.option.name?.toLowerCase() },
        },
      )

      return `${string}:`
    },

    /**
     * Compute the selected option data for the current option.
     * @returns {Object}
     */
    selectedOption() {
      return this.selectedOptions[this.option?.index]
    },

    /**
     * Compute selected option label.
     * @returns {String}
     */
    selectedOptionLabel() {
      return `${this.selectedOption?.name}: ${this.selectedOption?.value}`
    },

    /**
     * Compute text for swatch error
     * @returns {String}
     */
    swatchErrorText() {
      return this.$string(
        'product.swatch.swatch_error',
        {
          replace: { option: this.option.name?.toLowerCase() },
        },
      )
    },
  },

  watch: {

    /**
     * Watch selected value to emit update.
     * @param {String} value - Updated value.
     */
    selected(value) {
      this.handleOptionChange(value)
    },

    /**
     * Watch for changes in the selected options.
     * @param {Array} selectedOptions - Updated selected options.
     */
    selectedOptions(selectedOptions) {
      this.selected = selectedOptions[this.option.index]?.value
    },
  },

  mounted() {
    this.selected = this.selectedOptions[this.option.index]?.value
    this.handleEmptySwatches()

    if (this.product) {
      this.queryProduct(this.product.handle)
    }
  },

  methods: {

    /**
     * Handle swathes links on product card.
     * @param {String} colorOption - String of color option.
     * @returns {String}
     */
    href(colorOption) {
      if (!this.productCard) {
        return null
      }
    },

    /**
    * Handles the click event on a swatch.
    * If the component is part of a collection and swatchBehavior is false,
    * it prevents the default event and stops propagation, then updates the selected color
    * and queries the product.
    * Otherwise, it updates the selected color and queries the product.
    * @param {Event} event - The click event.
    * @param {String} colorOption - The selected color option.
    */
    handleSwatchClick(event, colorOption) {
      const currentVariant = this.getMatchVariant(colorOption)
      if (this.productCard) {
        if (!this.swatchBehavior) {
          window.location.href = currentVariant
          return
        }
        event.preventDefault()
        event.stopPropagation()
        this.selectedColor = colorOption
        this.queryProduct(this.product.handle)
      }
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
     async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }
      try {
        const response = await window.cnvs.Query({
          locale: cnvs.store.language,
          query: product,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })
        if (cnvs.settings.disableComponentReadyState) {
          return
        }
        this.updateSwatches(formatProduct(response))
      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Updates the swatches based on the selected product variant.
     * @param {Object} product - The product object containing variants and images.
     */
    updateSwatches(product) {
      const variant = product.variants.find((variantObject) => {
        return variantObject.selectedOptions.find((option) => {
          const optionName = option.name.toLowerCase()
          return optionName === 'colour' && option.value == this.selectedColor
        })
      }) || product.variants[0]

      const image = product.images.find((image) => {
         return image.id === variant.image && image.variant === variant.id
      })
      this.$emit('option-change', {
        variant: variant,
        variantId: variant.id,
        variantImage: image,
        variantPrice: variant.price.amount,
        variantComparePrice: variant.compare_at_price || variant.price.amount,
      })
    },

    /**
      * Finds the variant that matches the selected color option.
      * @param {String} colorOption - The color option to match.
      * @returns {String|null} - The URL of the matched variant or null if no match is found.
      */
    getMatchVariant(colorOption) {
      const matchVariant = this.product?.variants.find((variant) => {
        const colorMatchOptions = variant?.selectedOptions.find((selectedOption) => {
          const sanitizedOptionName = selectedOption.name.toLowerCase()
          const sanitizedOptionNameColorOption = this.$variable('productOptionsName.colorOption').toLowerCase()

          return (
            sanitizedOptionName === sanitizedOptionNameColorOption &&
            selectedOption.value === colorOption
          )
        })

        return colorMatchOptions
      })

      if (!matchVariant) {
        return null
      }

      return `${this.product.url}?variant=${matchVariant.id}`
    },

    /**
     * Handle change event on the swatch input element.
     * @param {String} value - String of selected option name.
     */
    handleOptionChange(value) {
      this.$emit('option-change', this.option.index, {
        name: this.option.name,
        value,
      })
    },

    /**
     * Gets CSS Classes for `product-swatches__item` element.
     * @returns {Object}
     */
    getSwatchItemClasses(value) {
      return {
        [this.$class('active')]: value === this.selectedOption?.value,
        [this.$class('disabled')]: this.isOptionUnavailable(value),
        'product-swatches__item--color': this.optionIsColor,
      }
    },

    /**
     * Gets CSS Styles for `product-swatches__item` element.
     * - Applies background colour and background image if option is a color.
     * @param {String} value - String of selected option name.
     * @returns {Object|Boolean}
     */
    getSwatchItemStyle(value) {
      if (!this.optionIsColor) {
        return false
      }

      /**
       * Refer to swatchOptions for English values to find correct swatch.
       */
      const valueIndex = this.option.values?.findIndex((item) => item === value)

      const swatchOption = this.options.find((_, index) => {
        return index === this.option.index
      })

      const swatchFileName = `Swatch ${swatchOption.values[valueIndex]}`

      const swatchImage = this.images.findIndex((image) => {
        return image?.alt?.toLowerCase().includes(swatchFileName.toLowerCase())
      })

      if (swatchImage === -1) {
        const cdnVariantName = swatchOption.values[valueIndex]
          .toLowerCase()
          .replace(/\s/g, '-')
          .replace(/\//g, '-')

        const cdnSwatchImage = cnvs.urls.files
          .replace('placeholder', `product_swatch_colour_${cdnVariantName}_20x.png`)
          .replace('v=', '')

        if (cdnSwatchImage) {
          return {
            backgroundImage: `url(${cdnSwatchImage})`,
          }
        }

        return false
      }

      const sanitizedAlt = this.images[swatchImage]?.alt?.toLowerCase()

      return {
        backgroundImage: sanitizedAlt && sanitizedAlt.includes(swatchFileName.toLowerCase())
          ? `url(${this.images[swatchImage].src})`
          : false,
      }
    },

    /**
     * Add gray border to swatches without images
     */
    handleEmptySwatches() {
      const swatchItems = this.$refs.swatchItems || []

      swatchItems.forEach(item => {
        if (item.style.backgroundImage) {
          return
        }
        item.classList.add(this.whiteSwatchClass)
      })
    },

    /**
     * Returns the option value disabled state.
     * - Gets variants and finds availability.
     * @param {String} optionValue - The value to check.
     * @returns {Boolean}
     */
    isOptionUnavailable(optionValue) {
      if (!this.product) {
        return false
      }

      const options = [...this.selectedOptions].map((option) => option.value)
      options[this.option.index] = optionValue

      const isAvailable = this.isVariantOptionAvailable(this.product, options)

      return !isAvailable
    },

    /**
     * Check if there is an available variant with the given option value.
     * @param {Object} product - The formatted GraphQL product response.
     * @param {String|String[]} optionValue - The option value or an array of
     * values.
     * @returns {boolean}
     */
    isVariantOptionAvailable(product, optionValue) {
      if (!product) {
        return true
      }

      let options = []

      if (typeof optionValue === 'string') {
        options.push(optionValue)
      } else if (Array.isArray(optionValue)) {
        options = optionValue.filter(Boolean)
      }

      if (product.isBoost) {
        const boostOptions = options
          .join('/')
          .split('/')
          .map(option => option.trim().toLowerCase())

        return product.variants
          .filter((variant) => {
            const selectedOptions = variant.title
              .split('/')
              .map((option) => {
                return option.trim().toLowerCase()
              })

            return boostOptions.every((option) => {
              return selectedOptions.indexOf(option) > -1
            }) && variant.available
          })
          .length > 0
      }

      return product.variants.filter(variant => {
        const valuesAreInSelectedOptions = options.every(value => {
          return variant.selectedOptions.some((option) => {
            return option.value.toLowerCase() === value.toLowerCase()
          })
        })

        return valuesAreInSelectedOptions && variant.available
      }).length > 0
    },

    /**
     * Finds the variants a given option value exists in.
     * @param {String} optionValue - The value of the option, e.g. 'Small'
     * @param {Number} index - The current option index.
     * @returns {Array}
     */
    getVariantsByOptionValue(optionValue, index) {
      return this.product?.variants?.filter((variant) => {
        if (index === 0) {
          return variant.selectedOptions[0].value === optionValue
        }

        if (index === 1) {
          if (!this.selectedOptions.length) {
            return true
          }
          return (
            variant.selectedOptions[0].value === this.selectedOptions[0]?.value &&
            variant.selectedOptions[1].value === optionValue
          )
        }

        if (index === 2) {
          if (this.selectedOptions.length < 2) {
            return true
          }

          return (
            variant.selectedOptions[0].value === this.selectedOptions[0]?.value &&
            variant.selectedOptions[1].value === this.selectedOptions[1]?.value &&
            variant.selectedOptions[2].value === optionValue
          )
        }

        return variant.selectedOptions[index] === optionValue
      })
    },

    /**
     * Compute if the swatch is considered white
     * @param {String} optionValue - The option value
     * @returns {Boolean}
     */
    isWhiteSwatch(optionValue) {
      const whiteSwatches = this.$variable('product.whiteSwatches')
      return whiteSwatches.indexOf(optionValue.toLowerCase()) > -1
    },
  },
}
</script>

<style lang="scss">
@import './product-swatches';
</style>
